import { c as _c } from "react/compiler-runtime";
import React, { useContext, useEffect, useRef, useState } from "react";
import { BarChart3, BookOpen, LifeBuoy, LogOut, Monitor, Moon, Palette, SettingsIcon, Sun, Wrench } from "lucide-react";
import { useTheme } from "next-themes";
import posthog from "posthog-js";
import { AuthContext } from "../AuthProvider";
import { NotificationFloatingButton } from "../NotificationFloatingButton";
import { StatusWidgetMorph, RailStatusHoverCard } from "./StatusIndicator";
import { BSErrorBoundary } from "../ui/bs-error-boundary";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@repo/blacksmith-ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "@repo/blacksmith-ui/tooltip";
import { Button } from "@repo/blacksmith-ui/button";
import { RoutePrefetchLink } from "@/components/ui/route-prefetch-link";
import { useSetPersistedTheme } from "@/hooks/useAppearanceSettings";
import { useIsHydrated } from "@/components/codesmith/useIsHydrated";
import { cn } from "@repo/blacksmith-ui/cn";
import { RAIL_REDESIGN_WIDTH, RAIL_WIDTH } from "./railConstants";
export type SidebarActiveSection = "actions" | "codesmith" | "usage" | "admin";
export const RAIL_ROW = cn("rail-row flex h-8 w-full items-center overflow-hidden rounded-md cursor-pointer transition-none", "type-body-s-regular text-neutral-800 hover:bg-black/[0.04] hover:text-neutral-950", "[&_svg]:text-neutral-600 hover:[&_svg]:text-neutral-900", "dark:text-neutral-300 dark:hover:bg-white/[0.04] dark:hover:text-white dark:[&_svg]:text-neutral-400 dark:hover:[&_svg]:text-white", "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-border)]");
export const RAIL_ROW_ACTIVE = "bg-[#0000000f] text-neutral-950 [&_svg]:text-neutral-900 dark:bg-white/6 dark:text-white dark:[&_svg]:text-white";
export const RAIL_REDESIGN_CLASS = "[&_.rail-row]:rounded-[4px] [&_.rail-row-icon]:w-full [&_.rail-row-icon>svg]:size-4";
export function RailRowContents(t0) {
  const $ = _c(15);
  const {
    icon,
    label,
    badge,
    expanded: t1,
    hideLabelWhenCollapsed: t2
  } = t0;
  const expanded = t1 === undefined ? true : t1;
  const hideLabelWhenCollapsed = t2 === undefined ? true : t2;
  const collapsedAndRemoved = hideLabelWhenCollapsed && !expanded;
  let t3;
  if ($[0] !== icon) {
    t3 = <span className="rail-row-icon flex size-8 shrink-0 items-center justify-center">{icon}</span>;
    $[0] = icon;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const t4 = collapsedAndRemoved && "hidden";
  const t5 = !badge && "pr-2";
  let t6;
  if ($[2] !== t4 || $[3] !== t5) {
    t6 = cn("whitespace-nowrap text-left", t4, t5);
    $[2] = t4;
    $[3] = t5;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  let t7;
  if ($[5] !== label || $[6] !== t6) {
    t7 = <span className={t6}>{label}</span>;
    $[5] = label;
    $[6] = t6;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== badge || $[9] !== collapsedAndRemoved) {
    t8 = badge && !collapsedAndRemoved ? <span className="ml-auto mr-2 shrink-0">{badge}</span> : null;
    $[8] = badge;
    $[9] = collapsedAndRemoved;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== t3 || $[12] !== t7 || $[13] !== t8) {
    t9 = <>{t3}{t7}{t8}</>;
    $[11] = t3;
    $[12] = t7;
    $[13] = t8;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  return t9;
}
const THEME_OPTIONS = [{
  value: "system",
  label: "System",
  icon: Monitor
}, {
  value: "light",
  label: "Light",
  icon: Sun
}, {
  value: "dark",
  label: "Dark",
  icon: Moon
}] as const;
export function AppearanceThemeToggle() {
  const $ = _c(5);
  const {
    theme
  } = useTheme();
  const setTheme = useSetPersistedTheme();
  const mounted = useIsHydrated();
  const activeTheme = mounted ? theme ?? "system" : "system";
  let t0;
  if ($[0] !== activeTheme || $[1] !== setTheme) {
    t0 = THEME_OPTIONS.map(t1 => {
      const {
        value,
        label,
        icon: Icon
      } = t1;
      const isActive = activeTheme === value;
      return <button key={value} type="button" aria-label={label} aria-pressed={isActive} onClick={() => setTheme(value)} className={cn("flex size-7 items-center justify-center rounded-md transition-colors", "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-border)]", isActive ? "bg-[#0000000f] text-neutral-950 dark:bg-white/6 dark:text-white" : "text-neutral-600 hover:bg-black/[0.04] hover:text-neutral-900 dark:text-neutral-400 dark:hover:bg-white/[0.04] dark:hover:text-white")}><Icon className="size-3.5" /></button>;
    });
    $[0] = activeTheme;
    $[1] = setTheme;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== t0) {
    t1 = <div className="-my-1 -mr-1 flex items-center gap-0.5">{t0}</div>;
    $[3] = t0;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
}

// A right-anchored label tooltip for a collapsed rail/sidebar row. Used both by
// the icon-collapsible sidebar and by the slim app-switcher rail (which no
// longer hover-expands to reveal labels, so each row surfaces its label here).
export function CollapsedIconTooltip(t0) {
  const $ = _c(7);
  const {
    label,
    enabled,
    children
  } = t0;
  if (!enabled) {
    return children;
  }
  let t1;
  if ($[0] !== children) {
    t1 = <TooltipTrigger asChild={true}>{children}</TooltipTrigger>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== label) {
    t2 = <TooltipContent side="right">{label}</TooltipContent>;
    $[2] = label;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <Tooltip>{t1}{t2}</Tooltip>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}
function useSidebarLogout() {
  const $ = _c(2);
  const {
    logout
  } = useContext(AuthContext);
  let t0;
  if ($[0] !== logout) {
    t0 = () => {
      posthog?.reset();
      logout();
    };
    $[0] = logout;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
export function SidebarAdminRow(t0) {
  const $ = _c(19);
  const {
    expanded,
    active,
    onClick,
    hideLabelWhenCollapsed: t1,
    divider: t2
  } = t0;
  const hideLabelWhenCollapsed = t1 === undefined ? true : t1;
  const divider = t2 === undefined ? true : t2;
  const {
    user
  } = useContext(AuthContext);
  if (!user?.is_superuser) {
    return null;
  }
  let t3;
  if ($[0] !== divider) {
    t3 = divider && <div className="my-2"><hr className="border-[#0c0c0d14] dark:border-white/12" /></div>;
    $[0] = divider;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const t4 = hideLabelWhenCollapsed && !expanded;
  const t5 = active ? "page" : undefined;
  const t6 = active && RAIL_ROW_ACTIVE;
  let t7;
  if ($[2] !== t6) {
    t7 = cn(RAIL_ROW, t6, "group-data-[collapsible=icon]:mx-auto group-data-[collapsible=icon]:!w-8 group-data-[collapsible=icon]:!min-w-8 group-data-[collapsible=icon]:!max-w-8 group-data-[collapsible=icon]:justify-center");
    $[2] = t6;
    $[3] = t7;
  } else {
    t7 = $[3];
  }
  let t8;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <Wrench className="size-[18px]" />;
    $[4] = t8;
  } else {
    t8 = $[4];
  }
  let t9;
  if ($[5] !== expanded || $[6] !== hideLabelWhenCollapsed) {
    t9 = <RailRowContents icon={t8} label="Admin" expanded={expanded} hideLabelWhenCollapsed={hideLabelWhenCollapsed} />;
    $[5] = expanded;
    $[6] = hideLabelWhenCollapsed;
    $[7] = t9;
  } else {
    t9 = $[7];
  }
  let t10;
  if ($[8] !== onClick || $[9] !== t5 || $[10] !== t7 || $[11] !== t9) {
    t10 = <RoutePrefetchLink href="/admin" onClick={onClick} aria-label="Admin" aria-current={t5} className={t7}>{t9}</RoutePrefetchLink>;
    $[8] = onClick;
    $[9] = t5;
    $[10] = t7;
    $[11] = t9;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  let t11;
  if ($[13] !== t10 || $[14] !== t4) {
    t11 = <CollapsedIconTooltip label="Admin" enabled={t4}>{t10}</CollapsedIconTooltip>;
    $[13] = t10;
    $[14] = t4;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  let t12;
  if ($[16] !== t11 || $[17] !== t3) {
    t12 = <>{t3}{t11}</>;
    $[16] = t11;
    $[17] = t3;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  return t12;
}
export function SidebarGlobalFooter(t0) {
  const $ = _c(94);
  const {
    expanded,
    currentOrg,
    settingsHref,
    activeSection,
    onSectionClick,
    className,
    hideLabelWhenCollapsed: t1,
    statusAsHoverCard: t2,
    redesign: t3,
    leading
  } = t0;
  const hideLabelWhenCollapsed = t1 === undefined ? true : t1;
  const statusAsHoverCard = t2 === undefined ? false : t2;
  const redesign = t3 === undefined ? false : t3;
  const {
    user
  } = useContext(AuthContext);
  const handleLogout = useSidebarLogout();
  const usageHref = currentOrg ? `/${currentOrg}/usage` : "/usage";
  const supportHref = currentOrg ? `/${currentOrg}/support` : "/support";
  const [settingsMenuOpen, setSettingsMenuOpen] = useState(false);
  const settingsTriggerRef = useRef(null);
  const settingsMenuCloseTimer = useRef(null);
  let t4;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      if (settingsMenuCloseTimer.current) {
        clearTimeout(settingsMenuCloseTimer.current);
        settingsMenuCloseTimer.current = null;
      }
    };
    $[0] = t4;
  } else {
    t4 = $[0];
  }
  const cancelSettingsMenuClose = t4;
  let t5;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      cancelSettingsMenuClose();
      setSettingsMenuOpen(true);
    };
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  const openSettingsMenu = t5;
  let t6;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      cancelSettingsMenuClose();
      settingsMenuCloseTimer.current = setTimeout(() => setSettingsMenuOpen(false), 120);
    };
    $[2] = t6;
  } else {
    t6 = $[2];
  }
  const scheduleSettingsMenuClose = t6;
  let t7;
  let t8;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => cancelSettingsMenuClose;
    t8 = [];
    $[3] = t7;
    $[4] = t8;
  } else {
    t7 = $[3];
    t8 = $[4];
  }
  useEffect(t7, t8);
  let t9;
  if ($[5] !== expanded || $[6] !== hideLabelWhenCollapsed) {
    t9 = <NotificationFloatingButton variant="sidebar" label="Notifications" expanded={expanded} hideLabelWhenCollapsed={hideLabelWhenCollapsed} />;
    $[5] = expanded;
    $[6] = hideLabelWhenCollapsed;
    $[7] = t9;
  } else {
    t9 = $[7];
  }
  const notifications = t9;
  const t10 = redesign ? undefined : "pt-1";
  let t11;
  if ($[8] !== expanded || $[9] !== redesign || $[10] !== statusAsHoverCard) {
    t11 = statusAsHoverCard ? <RailStatusHoverCard railWidth={redesign ? RAIL_REDESIGN_WIDTH : RAIL_WIDTH} /> : <StatusWidgetMorph expanded={expanded} />;
    $[8] = expanded;
    $[9] = redesign;
    $[10] = statusAsHoverCard;
    $[11] = t11;
  } else {
    t11 = $[11];
  }
  let t12;
  if ($[12] !== t10 || $[13] !== t11) {
    t12 = <BSErrorBoundary title="Status Indicator" fallback={null}><div className={t10}>{t11}</div></BSErrorBoundary>;
    $[12] = t10;
    $[13] = t11;
    $[14] = t12;
  } else {
    t12 = $[14];
  }
  const status = t12;
  const t13 = !!currentOrg;
  let t14;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = () => setSettingsMenuOpen(false);
    $[15] = t14;
  } else {
    t14 = $[15];
  }
  let t15;
  if ($[16] !== settingsMenuOpen) {
    t15 = event => {
      if (settingsMenuOpen && event.button === 0 && !event.ctrlKey) {
        event.preventDefault();
      }
    };
    $[16] = settingsMenuOpen;
    $[17] = t15;
  } else {
    t15 = $[17];
  }
  let t16;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <SettingsIcon className="size-[18px]" />;
    $[18] = t16;
  } else {
    t16 = $[18];
  }
  let t17;
  if ($[19] !== expanded || $[20] !== hideLabelWhenCollapsed) {
    t17 = <RailRowContents icon={t16} label="Settings" expanded={expanded} hideLabelWhenCollapsed={hideLabelWhenCollapsed} />;
    $[19] = expanded;
    $[20] = hideLabelWhenCollapsed;
    $[21] = t17;
  } else {
    t17 = $[21];
  }
  let t18;
  if ($[22] !== settingsHref || $[23] !== t13 || $[24] !== t15 || $[25] !== t17) {
    t18 = <DropdownMenuTrigger asChild={true}><RoutePrefetchLink ref={settingsTriggerRef} href={settingsHref} prefetchOnIntent={t13} aria-label="Settings menu" className={RAIL_ROW} onPointerEnter={openSettingsMenu} onPointerLeave={scheduleSettingsMenuClose} onClick={t14} onPointerDown={t15}>{t17}</RoutePrefetchLink></DropdownMenuTrigger>;
    $[22] = settingsHref;
    $[23] = t13;
    $[24] = t15;
    $[25] = t17;
    $[26] = t18;
  } else {
    t18 = $[26];
  }
  let t19;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = event_1 => {
      const target = event_1.target;
      if (target instanceof Node && settingsTriggerRef.current?.contains(target)) {
        event_1.preventDefault();
      }
    };
    $[27] = t19;
  } else {
    t19 = $[27];
  }
  const t20 = !!currentOrg;
  let t21;
  let t22;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <SettingsIcon className="size-[18px] shrink-0" />;
    t22 = <span>Settings</span>;
    $[28] = t21;
    $[29] = t22;
  } else {
    t21 = $[28];
    t22 = $[29];
  }
  let t23;
  if ($[30] !== settingsHref || $[31] !== t20) {
    t23 = <DropdownMenuItem asChild={true} className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 [&_svg]:text-neutral-600 hover:[&_svg]:text-neutral-900 focus:[&_svg]:text-neutral-900 dark:[&_svg]:text-neutral-400 dark:hover:[&_svg]:text-white dark:focus:[&_svg]:text-white"><RoutePrefetchLink href={settingsHref} prefetchOnIntent={t20}>{t21}{t22}</RoutePrefetchLink></DropdownMenuItem>;
    $[30] = settingsHref;
    $[31] = t20;
    $[32] = t23;
  } else {
    t23 = $[32];
  }
  const t24 = !!currentOrg;
  let t25;
  let t26;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <LifeBuoy className="size-[18px] shrink-0" />;
    t26 = <span>Contact Support</span>;
    $[33] = t25;
    $[34] = t26;
  } else {
    t25 = $[33];
    t26 = $[34];
  }
  let t27;
  if ($[35] !== supportHref || $[36] !== t24) {
    t27 = <DropdownMenuItem asChild={true} className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 [&_svg]:text-neutral-600 hover:[&_svg]:text-neutral-900 focus:[&_svg]:text-neutral-900 dark:[&_svg]:text-neutral-400 dark:hover:[&_svg]:text-white dark:focus:[&_svg]:text-white"><RoutePrefetchLink href={supportHref} prefetchOnIntent={t24}>{t25}{t26}</RoutePrefetchLink></DropdownMenuItem>;
    $[35] = supportHref;
    $[36] = t24;
    $[37] = t27;
  } else {
    t27 = $[37];
  }
  let t28;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = <DropdownMenuItem asChild={true} className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 [&_svg]:text-neutral-600 hover:[&_svg]:text-neutral-900 focus:[&_svg]:text-neutral-900 dark:[&_svg]:text-neutral-400 dark:hover:[&_svg]:text-white dark:focus:[&_svg]:text-white"><a href="https://docs.blacksmith.sh" target="_blank" rel="noopener noreferrer"><BookOpen className="size-[18px] shrink-0" /><span>Documentation...</span></a></DropdownMenuItem>;
    $[38] = t28;
  } else {
    t28 = $[38];
  }
  let t29;
  let t30;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <DropdownMenuItem onSelect={_temp2} className="flex cursor-default items-center justify-between gap-2 rounded-md px-2 py-1.5 hover:bg-transparent focus:bg-transparent data-[highlighted]:bg-transparent dark:hover:bg-transparent dark:focus:bg-transparent dark:data-[highlighted]:bg-transparent"><span className="flex items-center gap-2"><Palette className="size-[18px] shrink-0 text-neutral-600 dark:text-neutral-400" /><span>Appearance</span></span><AppearanceThemeToggle /></DropdownMenuItem>;
    t30 = <DropdownMenuSeparator />;
    $[39] = t29;
    $[40] = t30;
  } else {
    t29 = $[39];
    t30 = $[40];
  }
  let t31;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = <LogOut className="size-[18px] shrink-0" />;
    $[41] = t31;
  } else {
    t31 = $[41];
  }
  let t32;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = <div>Log out</div>;
    $[42] = t32;
  } else {
    t32 = $[42];
  }
  let t33;
  if ($[43] !== user) {
    t33 = user?.username ? <div className="text-[12px] leading-[16px] text-neutral-800 dark:text-white/50">{user.username}</div> : null;
    $[43] = user;
    $[44] = t33;
  } else {
    t33 = $[44];
  }
  let t34;
  if ($[45] !== t33) {
    t34 = <div className="leading-tight">{t32}{t33}</div>;
    $[45] = t33;
    $[46] = t34;
  } else {
    t34 = $[46];
  }
  let t35;
  if ($[47] !== handleLogout || $[48] !== t34) {
    t35 = <DropdownMenuItem className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 [&_svg]:text-neutral-600 hover:[&_svg]:text-neutral-900 focus:[&_svg]:text-neutral-900 dark:[&_svg]:text-neutral-400 dark:hover:[&_svg]:text-white dark:focus:[&_svg]:text-white" onClick={handleLogout}>{t31}{t34}</DropdownMenuItem>;
    $[47] = handleLogout;
    $[48] = t34;
    $[49] = t35;
  } else {
    t35 = $[49];
  }
  let t36;
  if ($[50] !== t23 || $[51] !== t27 || $[52] !== t35) {
    t36 = <DropdownMenuContent side="right" align="start" sideOffset={8} alignOffset={-36} className="w-[232px]" onPointerEnter={openSettingsMenu} onPointerLeave={scheduleSettingsMenuClose} onCloseAutoFocus={_temp} onPointerDownOutside={t19}>{t23}{t27}{t28}{t29}{t30}{t35}</DropdownMenuContent>;
    $[50] = t23;
    $[51] = t27;
    $[52] = t35;
    $[53] = t36;
  } else {
    t36 = $[53];
  }
  let t37;
  if ($[54] !== settingsMenuOpen || $[55] !== t18 || $[56] !== t36) {
    t37 = <DropdownMenu modal={false} open={settingsMenuOpen} onOpenChange={setSettingsMenuOpen}>{t18}{t36}</DropdownMenu>;
    $[54] = settingsMenuOpen;
    $[55] = t18;
    $[56] = t36;
    $[57] = t37;
  } else {
    t37 = $[57];
  }
  const settingsMenu = t37;
  const t38 = hideLabelWhenCollapsed && !expanded;
  const t39 = currentOrg ? true : null;
  const t40 = !!currentOrg;
  let t41;
  if ($[58] !== onSectionClick) {
    t41 = onSectionClick?.("usage");
    $[58] = onSectionClick;
    $[59] = t41;
  } else {
    t41 = $[59];
  }
  const t42 = activeSection === "usage" ? "page" : undefined;
  const t43 = activeSection === "usage" && RAIL_ROW_ACTIVE;
  let t44;
  if ($[60] !== t43) {
    t44 = cn(RAIL_ROW, t43);
    $[60] = t43;
    $[61] = t44;
  } else {
    t44 = $[61];
  }
  let t45;
  if ($[62] === Symbol.for("react.memo_cache_sentinel")) {
    t45 = <BarChart3 className="size-[18px]" />;
    $[62] = t45;
  } else {
    t45 = $[62];
  }
  let t46;
  if ($[63] !== expanded || $[64] !== hideLabelWhenCollapsed) {
    t46 = <RailRowContents icon={t45} label="Usage and Billing" expanded={expanded} hideLabelWhenCollapsed={hideLabelWhenCollapsed} />;
    $[63] = expanded;
    $[64] = hideLabelWhenCollapsed;
    $[65] = t46;
  } else {
    t46 = $[65];
  }
  let t47;
  if ($[66] !== t39 || $[67] !== t40 || $[68] !== t41 || $[69] !== t42 || $[70] !== t44 || $[71] !== t46 || $[72] !== usageHref) {
    t47 = <RoutePrefetchLink href={usageHref} prefetch={t39} prefetchOnIntent={t40} onClick={t41} aria-label="Usage and Billing" aria-current={t42} className={t44}>{t46}</RoutePrefetchLink>;
    $[66] = t39;
    $[67] = t40;
    $[68] = t41;
    $[69] = t42;
    $[70] = t44;
    $[71] = t46;
    $[72] = usageHref;
    $[73] = t47;
  } else {
    t47 = $[73];
  }
  let t48;
  if ($[74] !== t38 || $[75] !== t47) {
    t48 = <CollapsedIconTooltip label="Usage and Billing" enabled={t38}>{t47}</CollapsedIconTooltip>;
    $[74] = t38;
    $[75] = t47;
    $[76] = t48;
  } else {
    t48 = $[76];
  }
  const usage = t48;
  if (redesign) {
    let t49;
    if ($[77] !== className) {
      t49 = cn("rail-rows flex flex-col", className);
      $[77] = className;
      $[78] = t49;
    } else {
      t49 = $[78];
    }
    let t50;
    if ($[79] !== leading || $[80] !== notifications || $[81] !== settingsMenu || $[82] !== status || $[83] !== t49 || $[84] !== usage) {
      t50 = <div className={t49}>{leading}{usage}{notifications}{settingsMenu}{status}</div>;
      $[79] = leading;
      $[80] = notifications;
      $[81] = settingsMenu;
      $[82] = status;
      $[83] = t49;
      $[84] = usage;
      $[85] = t50;
    } else {
      t50 = $[85];
    }
    return t50;
  }
  let t49;
  if ($[86] !== className) {
    t49 = cn("rail-rows flex flex-col gap-1", className);
    $[86] = className;
    $[87] = t49;
  } else {
    t49 = $[87];
  }
  let t50;
  if ($[88] !== notifications || $[89] !== settingsMenu || $[90] !== status || $[91] !== t49 || $[92] !== usage) {
    t50 = <div className={t49}>{usage}{settingsMenu}{notifications}{status}</div>;
    $[88] = notifications;
    $[89] = settingsMenu;
    $[90] = status;
    $[91] = t49;
    $[92] = usage;
    $[93] = t50;
  } else {
    t50 = $[93];
  }
  return t50;
}
function _temp2(event_2) {
  return event_2.preventDefault();
}
function _temp(event_0) {
  return event_0.preventDefault();
}